/*
 * Default Select styles
 */
.Select {
  appearance: none;
  color: var(--brand-control-color-fg-default);
  cursor: pointer;
  background-color: var(--brand-color-canvas-default);
  border: none;
  border-radius: var(--brand-borderRadius-medium);
  line-height: var(--brand-text-lineHeight-200);
  transition: var(--brand-control-animation-duration) var(--brand-control-animation-easing);
  transition-property: color, background-color, box-shadow, border-color;
  width: 100%;
  grid-column: 1 / -1;
  grid-row: 1;
}

@media screen and (forced-colors: active) {
  /* Maintain dark bg color in Firefox on Windows high-contrast mode */
  .Select:disabled {
    background-color: -moz-combobox;
  }
}

.Select::placeholder {
  color: var(--brand-color-text-muted);
  opacity: 1;
}

.Select:focus-visible {
  border-color: var(--brand-control-color-focus);
  outline: none;
  box-shadow: 0 0 0 2px var(--brand-control-color-focus);
}

@media (forced-colors: active) {
  .Select:focus-visible {
    outline: var(--brand-borderWidth-thick) solid Highlight;
    outline-offset: var(--base-size-2);
  }
}

.Select[disabled] {
  -webkit-text-fill-color: var(--brand-control-color-fg-disabled);
  color: var(--brand-control-color-fg-disabled);
  cursor: not-allowed;
  background-color: var(--brand-control-color-canvas-disabled);
  border-color: var(--brand-control-color-border-disabled);
  opacity: 1;
}

.Select-wrapper--disabled.Select-wrapper::after {
  background-color: var(--brand-control-color-fg-disabled);
}

/*
 * Sizes
 */

.Select-wrapper--medium {
  height: var(--brand-control-medium-size);
}

.Select-wrapper--medium::after {
  margin-inline-end: calc(var(--brand-control-medium-paddingInline-condensed) / 2);
}

.Select--medium {
  font-weight: var(--brand-text-weight-100);
  font-size: var(--brand-text-size-100);
  padding-inline: var(--brand-control-medium-paddingInline-condensed);
  padding-inline-end: calc(var(--brand-control-medium-paddingInline-condensed) + var(--base-size-16));
  padding-block: 0;
}

.Select-wrapper--large {
  height: var(--brand-control-large-size);
}

.Select-wrapper--large::after {
  margin-inline-end: var(--base-size-8);
}

.Select--large {
  font-weight: var(--brand-text-weight-200);
  font-size: var(--brand-text-size-200);
  padding-inline: var(--brand-control-large-paddingInline-condensed);
  padding-inline-end: calc(var(--brand-control-large-paddingInline-condensed) + var(--base-size-16));
  padding-block: 0;
}

/*
 * Select wrapper
 */

.Select-wrapper {
  border: solid var(--brand-borderWidth-thin, 1px) var(--brand-control-color-border-default);
  border-radius: var(--brand-borderRadius-medium, 6px);
  font-family: var(--brand-body-fontFamily);
  position: relative;
  display: inline-grid;
  grid-template-columns: minmax(0, auto) var(--base-size-16);
}

.Select-wrapper::after {
  content: '';
  position: absolute;
  right: 0;
  width: var(--base-size-16);
  height: var(--base-size-16);
  pointer-events: none;
  background-color: var(--brand-control-color-fg-default);
  mask: url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTYiIGhlaWdodD0iMTYiIHZpZXdCb3g9IjAgMCAxNiAxNiIgZmlsbD0iIzU4NjA2OSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cGF0aCBkPSJNNC40MjcgOS40MjdsMy4zOTYgMy4zOTZhLjI1MS4yNTEgMCAwMC4zNTQgMGwzLjM5Ni0zLjM5NkEuMjUuMjUgMCAwMDExLjM5NiA5SDQuNjA0YS4yNS4yNSAwIDAwLS4xNzcuNDI3ek00LjQyMyA2LjQ3TDcuODIgMy4wNzJhLjI1LjI1IDAgMDEuMzU0IDBMMTEuNTcgNi40N2EuMjUuMjUgMCAwMS0uMTc3LjQyN0g0LjZhLjI1LjI1IDAgMDEtLjE3Ny0uNDI3eiIgLz48L3N2Zz4=');
  -webkit-mask: url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTYiIGhlaWdodD0iMTYiIHZpZXdCb3g9IjAgMCAxNiAxNiIgZmlsbD0iIzU4NjA2OSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cGF0aCBkPSJNNC40MjcgOS40MjdsMy4zOTYgMy4zOTZhLjI1MS4yNTEgMCAwMC4zNTQgMGwzLjM5Ni0zLjM5NkEuMjUuMjUgMCAwMDExLjM5NiA5SDQuNjA0YS4yNS4yNSAwIDAwLS4xNzcuNDI3ek00LjQyMyA2LjQ3TDcuODIgMy4wNzJhLjI1LjI1IDAgMDEuMzU0IDBMMTEuNTcgNi40N2EuMjUuMjUgMCAwMS0uMTc3LjQyN0g0LjZhLjI1LjI1IDAgMDEtLjE3Ny0uNDI3eiIgLz48L3N2Zz4=');
  mask-size: contain;
  -webkit-mask-size: contain;
  mask-repeat: no-repeat;
  -webkit-mask-repeat: no-repeat;
  grid-column: 2;
  grid-row: 1;
  place-self: center flex-end;
}

.Select-wrapper--disabled {
  border-color: var(--brand-control-color-border-disabled);
}

.Select-wrapper--fullWidth {
  width: 100%;
}

/*
 * Validation
 */

.Select--success {
  border-color: var(--brand-color-success-fg);
}

.Select--error {
  border-color: var(--brand-color-danger-fg);
}

/* Remove all animations, transitions and smooth scroll for people that prefer not to see them */
@media (prefers-reduced-motion: reduce) {
  .Select,
  .Select::before,
  .Select::after,
  .Select * {
    animation: none !important;
    transition: none !important;
    scroll-behavior: auto !important;
  }
}
